import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";

const services = [
  {
    icon: "ri-layout-grid-line",
    title: "影视分类浏览",
    desc: "覆盖电影、电视剧、综艺三大类别，支持按类型、地区、年份与排序方式筛选，帮助用户快速定位想看的内容。",
  },
  {
    icon: "ri-search-eye-line",
    title: "关键词搜索",
    desc: "一站检索影视作品与资讯内容，输入作品名称、类型、演员或关键词即可获得相关结果。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "收藏与片单",
    desc: "遇到心仪的作品可一键收藏，方便整理个人片单，随时回顾感兴趣的内容。",
  },
  {
    icon: "ri-newspaper-line",
    title: "影视资讯",
    desc: "持续更新影片动态、剧集观察与行业分析，帮助用户及时了解影视圈的最新变化。",
  },
];

const contacts = [
  { icon: "ri-mail-line", label: "合作邮箱", value: "hi@91peach.example" },
  { icon: "ri-phone-line", label: "客服电话", value: "400-000-9191" },
  { icon: "ri-map-pin-line", label: "办公地址", value: "北京市朝阳区影视产业园 8 号楼" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 91蜜桃",
    description:
      "了解 91蜜桃：一个专注最新影视资源精选与资讯的内容平台，提供分类浏览、关键词搜索、收藏片单与影视资讯服务。",
    keywords: "关于91蜜桃,91蜜桃介绍,影视平台,影视资讯,影视资源",
  });

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
            关于 91蜜桃
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-500">
            一个专注影视资源精选与资讯的内容平台，让寻找好作品这件事变得更简单。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <h2 className="font-heading text-xl font-black tracking-tight text-foreground-950 md:text-2xl">
              我们是谁
            </h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              91蜜桃以“最新影视资源精选与资讯平台”为核心定位，面向热爱电影、电视剧与综艺的广大观众。
              我们持续收录并整理优质影视作品资料，提供分类浏览、关键词检索与专题合集，
              帮助用户在丰富的内容中更快找到想看的那一部。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们相信，好的内容值得被认真对待。因此站内所有作品资料与资讯均经过整理与校对，
              力求信息准确、描述清晰，让每一次浏览都更有价值。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { value: "12,000+", label: "影视作品" },
                { value: "1,800+", label: "资讯文章" },
                { value: "9 年", label: "持续运营" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 text-center"
                >
                  <p className="font-heading text-lg font-black text-primary-600 md:text-xl">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-sm">
            <div className="h-56 w-full overflow-hidden bg-background-200 md:h-72">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20film%20and%20media%20studio%20interior%2C%20warm%20ambient%20lighting%20over%20a%20long%20table%20of%20film%20reels%20and%20screens%20showing%20movie%20scenes%2C%20editorial%20photography%2C%20orange%20accent%20tones%2C%20clean%20minimal%20composition&width=900&height=600&seq=about-91peach-1&orientation=landscape"
                alt="91蜜桃影视内容团队工作场景"
                title="91蜜桃 - 影视内容平台"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="p-5">
              <p className="text-sm leading-relaxed text-foreground-600">
                “我们希望 91蜜桃 不只是一个资源站，而是一个能让影迷安心浏览、
                快速了解影视作品的资讯与资料平台。”
              </p>
              <p className="mt-3 text-xs font-medium text-foreground-400">
                —— 91蜜桃 编辑部
              </p>
            </div>
          </div>
        </section>

        <section className="mt-16">
          <SectionHeading
            eyebrow="服务内容"
            title="我们能为你做什么"
            desc="围绕影视作品的查找、浏览与了解，我们提供以下核心服务。"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5">
            {services.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-xl text-primary-600">
                  <i className={item.icon} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-500">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16">
          <SectionHeading eyebrow="联系方式" title="与我们取得联系" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:gap-5">
            {contacts.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-lg text-secondary-800">
                  <i className={item.icon} />
                </span>
                <p className="mt-3 text-xs text-foreground-400">{item.label}</p>
                <p className="mt-0.5 text-sm font-medium text-foreground-900">
                  {item.value}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="flex items-center gap-2">
            <i className="ri-shield-check-line text-xl text-primary-600" />
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              法律声明与版权信息
            </h2>
          </div>
          <div className="mt-4 space-y-3 text-sm leading-7 text-foreground-600">
            <p>
              本网站仅提供影视作品资料与资讯展示，不提供任何在线播放、下载或资源存储服务。
              站内所有作品信息、海报与文字内容均来源于公开资料整理，版权归原作者及权利方所有。
            </p>
            <p>
              如相关权利人认为本站内容侵犯其合法权益，请通过上方邮箱与我们联系，我们将第一时间核实并处理。
              用户在使用本站服务时，应遵守相关法律法规，尊重知识产权，在正版渠道观看影视作品。
            </p>
            <p className="text-foreground-500">
              © 91蜜桃 保留所有权利 · 京ICP备0000000号
            </p>
          </div>
          <Link
            to="/"
            className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </section>
      </div>
    </div>
  );
}